import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import { Box, debounce, Stack, SvgIcon, TablePagination } from '@mui/material';
import { DatasetsService } from 'services';
import DatasetsInventoryList from './components/DatasetsInventoryList';
import PageHeader from 'shared/components/PageHeader';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as Upload } from 'assets/images/ic_upload.svg';
import DialogBulkUpload from '../DialogBulkUpload';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';
import { BaseApplicationState } from 'state/baseapplication/Reducer';

const Datasets = (): ReactElement => {
  const dispatch = useDispatch();
  const datasetPagination = useSelector(
    (state: IAppState) => state.paginationState.dataset
  );
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [openBulk, setOpenBulk] = useState(false);
  const navigate = useNavigate();
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );

  // API calls
  const loadDatasets = (): void => {
    setLoading(true);
    const queryString = new URLSearchParams({
      offset: String(datasetPagination.offset),
      limit: String(datasetPagination.pageLimit),
      name: datasetPagination.search,
      status: datasetPagination.status,
    }).toString();
    DatasetsService.getDatasets(queryString)
      .then((response) => {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const { datasets, pagination } = response.data.data;
        if (datasets.length > 0) {
          setData(datasets);

          dispatch(
            PaginationActions.setPagination({
              dataset: {
                totalRecords: pagination.total,
                offset: pagination.offset,
                pageLimit: pagination.limit,
                totalPages: pagination.totalPages,
              },
            })
          );
        } else {
          setData([]);
        }
      })
      .catch(() => {
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const debouncSearch = useCallback(
    debounce((searchValue: any) => {
      dispatch(
        PaginationActions.setPagination({
          dataset: {
            ...datasetPagination,
            offset: 1,
            search: searchValue,
          },
        })
      );
    }, 500),
    [datasetPagination]
  );

  // Instance Methods
  const handlePageChange = (_: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        dataset: {
          offset: page + 1,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        dataset: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  // Hooks
  useEffect(() => {
    loadDatasets();
  }, [
    datasetPagination.offset,
    datasetPagination.pageLimit,
    datasetPagination.search,
    datasetPagination.status,
  ]);

  // UI Elements
  return (
    <>
      <PageHeader
        title="Dataset Inventory"
        actions={
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
            {orgACLConfig.aiInventory?.includes(
              'aiInventory.datasets.bulkUpload'
            ) && (
              <AppPrimaryButton
                className="btn-small primary-light-fill"
                title="Bulk Upload"
                onClick={() => {
                  setOpenBulk(true);
                }}
                startIcon={
                  <SvgIcon
                    component={Upload}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#3D3C42"
                  />
                }
              />
            )}
            {orgACLConfig.aiInventory?.includes(
              'aiInventory.datasets.newDataset'
            ) && (
              <AppPrimaryButton
                className="btn-small"
                title="New Dataset"
                onClick={() => {
                  navigate('/datasets/new');
                }}
              />
            )}
          </Stack>
        }
      />
      <Box className="ai-inventory datasets">
        <HeadTag title="Datasets" />

        <Box className="dataset-inventory-wrapper">
          <DatasetsInventoryList
            inventoryData={data}
            onSearch={debouncSearch}
            loading={isLoading}
          />

          {!isLoading && data && data.length > 0 && (
            <TablePagination
              component="div"
              count={datasetPagination.totalRecords}
              page={datasetPagination.offset - 1}
              rowsPerPage={datasetPagination.pageLimit}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50]}
              disabled={isLoading}
            />
          )}
        </Box>
      </Box>
      {openBulk && (
        <DialogBulkUpload
          openBulk={openBulk}
          onClose={(reload: boolean) => {
            if (reload) {
              loadDatasets();
            }
            setOpenBulk(false);
          }}
          title="Upload Dataset File"
          variant="dataset"
        />
      )}
    </>
  );
};

export default Datasets;
